import React, { useEffect, useState } from 'react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, 
  BarChart, Bar
} from 'recharts';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { 
  ShieldAlert, Globe, Activity, Zap, Target, 
  Users, Layers, Crosshair
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// --- CONFIGURATION ---
const API_URL = 'https://whiteintel.io/api/regular/app/v2/threatfeed/get_dashboard.php'; 
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// --- CUSTOM THEME (Matching your RGB 13 22 35) ---
const THEME = {
  // The exact background you requested
  bgMain: "#000414",      
  
  // Slightly lighter for card headers/borders to create separation without changing hue
  cardBg: "#000414",      
  cardHeader: "#0a1224",  
  border: "#1e293b",      // Slate-800 (Subtle blue-grey border)
  
  // Text Colors
  textMain: "#f1f5f9",    // Slate-100
  textMuted: "#94a3b8",   // Slate-400
  
  // Accents
  accent: "#ef4444",      // Red-500
  primary: "#38bdf8",     // Sky-400 (Better visibility on dark blue than deep blue)
  grid: "#1e293b"         // Grid lines match border
};

// --- COMPONENTS ---

// 1. Enterprise Card (Bordered, Same BG as App)
const Card = ({ title, icon: Icon, children, action, className = "" }) => (
  <div 
    className={`flex flex-col border transition-all ${className}`}
    style={{ 
      backgroundColor: THEME.cardBg, 
      borderColor: THEME.border 
    }}
  >
    {/* Card Header */}
    <div 
      className="px-4 py-3 border-b flex justify-between items-center"
      style={{ 
        backgroundColor: THEME.cardHeader, 
        borderColor: THEME.border 
      }}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} style={{ color: THEME.accent }} />}
        <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: THEME.textMuted }}>
          {title}
        </span>
      </div>
      {action}
    </div>
    
    {/* Card Body */}
    <div className="flex-1 relative overflow-hidden">
      {children}
    </div>
  </div>
);

// 2. Metric Tile (Top Row)
const MetricTile = ({ label, value, trend, icon: Icon }) => (
  <div 
    className="p-4 flex items-start justify-between border-r last:border-r-0"
    style={{ 
      backgroundColor: THEME.cardBg, 
      borderColor: THEME.border 
    }}
  >
    <div>
      <div className="text-[10px] uppercase font-bold tracking-wider mb-1" style={{ color: THEME.textMuted }}>
        {label}
      </div>
      <div className="text-2xl font-mono font-bold" style={{ color: THEME.textMain }}>
        {value}
      </div>
      {trend && <div className="text-xs mt-1 font-mono" style={{ color: THEME.accent }}>{trend}</div>}
    </div>
    <div className="p-2 rounded border" style={{ borderColor: THEME.border, color: THEME.textMuted }}>
      <Icon size={18} />
    </div>
  </div>
);

// 3. Custom Tooltip
const MapTooltip = ({ content, x, y }) => {
  if (!content) return null;
  return (
    <div 
      className="fixed z-50 pointer-events-none px-3 py-2 shadow-2xl border"
      style={{ 
        left: x + 15, 
        top: y + 15,
        backgroundColor: THEME.bgMain,
        borderColor: THEME.accent
      }}
    >
      <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: THEME.textMain }}>{content.name}</div>
      <div className="text-xs font-mono" style={{ color: THEME.accent }}>{content.value} DETECTIONS</div>
    </div>
  );
};

// 4. Auth Helper
function getJwtToken() {
  try {
    return localStorage.getItem("jwtToken") || localStorage.getItem("token") || window.WHITEINTEL_TOKEN || "";
  } catch { return ""; }
}

const ThreatFeedDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState({ show: false, content: null, x: 0, y: 0 });

  useEffect(() => {
    fetch(API_URL, {
      method: 'GET', // Explicitly GET
      headers: { 'Authorization': `Bearer ${getJwtToken()}` }
    })
      .then(res => res.json())
      .then(json => {
        setData(json);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  if (loading || !data) return (
    <div className="h-96 flex items-center justify-center" style={{ backgroundColor: THEME.bgMain }}>
      <div className="flex flex-col items-center gap-4">
        <span className="font-mono text-xs uppercase tracking-widest" style={{ color: THEME.textMuted }}>Loading Intel...</span>
      </div>
    </div>
  );

  // --- Data Prep ---
  const mapData = data.widgets?.map || [];
  const maxAttacks = Math.max(...(mapData.map(d => parseInt(d.value)) || [0]));
  
  // Scale: Dark Blue-Grey -> Bright Red
  const colorScale = scaleLinear()
    .domain([0, maxAttacks || 1])
    .range([THEME.cardHeader, THEME.accent]); 

  return (
    <div className="w-full pb-8 font-sans" style={{ backgroundColor: THEME.bgMain }}>
      
      {/* TOOLTIP */}
      {tooltip.show && <MapTooltip content={tooltip.content} x={tooltip.x} y={tooltip.y} />}

      {/* --- TOP METRICS ROW --- */}
      {/* We use a flex container with borders to look like a single control strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-b-0" style={{ borderColor: THEME.border }}>
        <MetricTile label="Total Events (24h)" value={data.kpi?.total_events || 0} icon={Activity} trend="+12% vs avg" />
        <MetricTile 
    label="Active Adversaries (30d)" // Updated Label
    value={data.kpi?.active_adversaries || 0} // Uses the real count from DB
    icon={Users} 
/>
        <MetricTile label="Targeted Sectors" value={data.kpi?.active_sectors || 0} icon={Layers} />
        <MetricTile label="Critical IOCs" value={data.widgets?.latest_victims?.length || 0} icon={ShieldAlert} trend="Live Feed" />
      </div>

      {/* --- MAIN GRID --- */}
      <div className="border-l border-t" style={{ borderColor: THEME.border }}> {/* Outer wrapper border */}
        
        <div className="grid grid-cols-12">

          {/* ROW 1: MAP + FEED */}
          
          {/* MAP (8 cols) */}
          <div className="col-span-12 lg:col-span-8 h-[500px] border-r border-b" style={{ borderColor: THEME.border }}>
            <Card title="Global Threat Heatmap (Underground Forums & Chatter)" icon={Globe} className="h-full border-0">
              <div className="w-full h-full" style={{ backgroundColor: THEME.bgMain }}>
                <ComposableMap 
                  projectionConfig={{ scale: 155, rotate: [-10, 0, 0] }} 
                  style={{ width: "100%", height: "100%" }}
                >
                  <ZoomableGroup>
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          if (!geo.properties.name) return null;
                          const d = mapData.find((s) => s.id === geo.properties.name);
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={d ? colorScale(d.value) : "#1e293b"} // Default is Slate-800
                              stroke={THEME.bgMain}
                              strokeWidth={0.5}
                              style={{
                                default: { outline: "none" },
                                hover: { fill: "#ffffff", outline: "none", cursor: "crosshair" },
                                pressed: { outline: "none" },
                              }}
                              onMouseEnter={(evt) => {
                                const val = d ? d.value : 0;
                                setTooltip({
                                  show: true,
                                  content: { name: geo.properties.name, value: val },
                                  x: evt.clientX,
                                  y: evt.clientY
                                });
                              }}
                              onMouseMove={(evt) => {
                                setTooltip(prev => ({ ...prev, x: evt.clientX, y: evt.clientY }));
                              }}
                              onMouseLeave={() => setTooltip(prev => ({ ...prev, show: false }))}
                            />
                          );
                        })
                      }
                    </Geographies>
                  </ZoomableGroup>
                </ComposableMap>

                {/* Legend */}
                <div 
                    className="absolute bottom-4 left-4 p-2 flex flex-col gap-1 border"
                    style={{ backgroundColor: THEME.bgMain, borderColor: THEME.border }}
                >
                  <span className="text-[10px] uppercase font-bold" style={{ color: THEME.textMuted }}>Intensity</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2" style={{ backgroundColor: THEME.cardHeader }}></div>
                    <span className="text-[10px]" style={{ color: THEME.textMuted }}>Low</span>
                    <div className="w-2 h-2" style={{ backgroundColor: THEME.accent }}></div>
                    <span className="text-[10px]" style={{ color: THEME.textMuted }}>Crit</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* FEED (4 cols) */}
          <div className="col-span-12 lg:col-span-4 h-[500px] border-r border-b" style={{ borderColor: THEME.border }}>
            <Card 
              title="Intercepted Intel" 
              icon={Zap} 
              className="h-full border-0" 
              action={<span className="flex h-2 w-2 rounded-full bg-red-500 animate-pulse"/>}
            >
              <div className="overflow-y-auto h-full scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-10 text-[10px] uppercase font-bold" style={{ backgroundColor: THEME.cardHeader, color: THEME.textMuted }}>
                    <tr>
                      <th className="p-3 border-b" style={{ borderColor: THEME.border }}>Target</th>
                      <th className="p-3 border-b text-right" style={{ borderColor: THEME.border }}>Time</th>
                    </tr>
                  </thead>
                  <tbody className="text-xs font-mono">
                    {data.widgets.latest_victims.map((victim, i) => (
                      <tr key={i} className="border-b transition-colors cursor-pointer hover:bg-white/5" style={{ borderColor: THEME.border }}>
                        <td className="p-3">
                          <div className="font-bold truncate max-w-[180px]" style={{ color: THEME.textMain }}>
                            {victim.victim_name || "Unknown Target"}
                          </div>
                          <div className="flex items-center gap-2 mt-1" style={{ color: THEME.textMuted }}>
                            <span className="text-[10px] border px-1 rounded" style={{ borderColor: THEME.border }}>{victim.attack_type}</span>
                            <span className="truncate max-w-[100px]" style={{ color: THEME.accent }}>{victim.actor_name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-right" style={{ color: THEME.textMuted }}>
                          {formatDistanceToNow(new Date(victim.published_at), { addSuffix: true }).replace("about ", "")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>


          {/* ROW 2: CHARTS */}

          {/* TIMELINE (6 cols) */}
          <div className="col-span-12 lg:col-span-6 h-[320px] border-r border-b" style={{ borderColor: THEME.border }}>
            <Card title="Attack Volume (30 Days)" icon={Crosshair} className="h-full border-0">
              <div className="w-full h-full p-4 pl-0">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.widgets.timeline}>
                    <defs>
                      <linearGradient id="chartColor" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={THEME.accent} stopOpacity={0.2}/>
                        <stop offset="95%" stopColor={THEME.accent} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={THEME.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tick={{fill: THEME.textMuted, fontSize: 10, fontFamily: 'monospace'}} 
                      axisLine={false} tickLine={false} minTickGap={30}
                    />
                    <YAxis 
                      tick={{fill: THEME.textMuted, fontSize: 10, fontFamily: 'monospace'}} 
                      axisLine={false} tickLine={false}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: THEME.bgMain, borderColor: THEME.accent, color: THEME.textMain }}
                      itemStyle={{ color: THEME.accent, fontFamily: 'monospace' }}
                    />
                    <Area type="monotone" dataKey="count" stroke={THEME.accent} fillOpacity={1} fill="url(#chartColor)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* ACTORS (3 cols) */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3 h-[320px] border-r border-b" style={{ borderColor: THEME.border }}>
            <Card title="Active Threat Actors" icon={Users} className="h-full border-0">
               <div className="flex flex-col h-full overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700">
                  {data.widgets.actors.map((actor, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border-b hover:bg-white/5" style={{ borderColor: THEME.border }}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs" style={{ color: THEME.textMuted }}>#{i+1}</span>
                        <span className="text-sm font-bold" style={{ color: THEME.textMain }}>{actor.actor_name}</span>
                      </div>
                      <div className="font-mono text-xs font-bold" style={{ color: THEME.accent }}>
                        {actor.attack_count}
                      </div>
                    </div>
                  ))}
               </div>
            </Card>
          </div>

          {/* INDUSTRIES (3 cols) */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3 h-[320px] border-b" style={{ borderColor: THEME.border }}>
            <Card title="Victimology by Sector" icon={Target} className="h-full border-0">
              <div className="w-full h-full p-4 pl-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={data.widgets.industries.slice(0, 8)} margin={{ left: 10 }}>
                    <CartesianGrid stroke={THEME.border} horizontal={false} />
                    <XAxis type="number" hide />
                    <YAxis 
                      dataKey="industry_name" 
                      type="category" 
                      width={100} 
                      tick={{fill: THEME.textMuted, fontSize: 11, fontWeight: 500}} 
                      interval={0}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip 
                      cursor={{fill: 'transparent'}} 
                      contentStyle={{ backgroundColor: THEME.bgMain, borderColor: THEME.primary, color: THEME.textMain }}
                    />
                    <Bar dataKey="attack_count" fill={THEME.primary} barSize={16} radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ThreatFeedDashboard;